import {
    DatacentralHookForm,
    FormField,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    createCompany,
    lookupCompanyByTaxCode,
} from '@/modules/Company/services'
import { INDUSTRY_OPTIONS } from '@/modules/Customer/constants'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs, { Dayjs } from 'dayjs'
import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem } from 'vnetwork-ui'
import createCompanyValidate from './yup'

export interface CreateCompanyFormValues {
    company_name: string
    company_tax: string
    company_address: string
    company_website: string
    company_industry: string
    company_establish_date?: string | Date | Dayjs | null
}

export interface CreateCompanyProps extends ModalProps {
    input?: string
    inputTaxCode?: string
    onSuccessCreateCompany: (
        data: Awaited<ReturnType<typeof createCompany>>
    ) => void
}

const CreateCompany: React.FC<CreateCompanyProps> = ({
    onClose,
    open,
    input,
    inputTaxCode,
    onSuccessCreateCompany,
}) => {
    const { t } = useTranslation()
    const [isLookingUp, setIsLookingUp] = useState(false)
    const debounceRef = useRef<NodeJS.Timeout | null>(null)

    const formControl = useForm<CreateCompanyFormValues>({
        defaultValues: {
            company_address: '',
            company_name: input || '',
            company_tax: inputTaxCode || '',
            company_establish_date: undefined,
            company_website: '',
            company_industry: '',
        },
        resolver: yupResolver(createCompanyValidate as any),
    })

    // Auto-fill company info when tax code changes
    const taxValue = useWatch({
        control: formControl.control,
        name: 'company_tax',
    })

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        if (!taxValue || taxValue.trim().length < 10) return

        debounceRef.current = setTimeout(async () => {
            setIsLookingUp(true)
            try {
                const res = await lookupCompanyByTaxCode(taxValue.trim())
                const data = res?.result
                if (data) {
                    if (data.name)
                        formControl.setValue('company_name', data.name)
                    if (data.address)
                        formControl.setValue('company_address', data.address)
                }
            } catch {
                // Lookup failed — user can still fill manually
            } finally {
                setIsLookingUp(false)
            }
        }, 500)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [taxValue, formControl])

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createCompany },
        {
            reactQueryKeys: QUERY_KEYS.COMPANIES,
            showSuccessToast: false,
            onSuccess: (data) => onSuccessCreateCompany(data),
        }
    )

    const handleSubmitForm = (data: CreateCompanyFormValues) => {
        mutateAsync({
            company: {
                ...data,
                company_establish_date: data.company_establish_date
                    ? `${dayjs(data.company_establish_date).format(
                          'YYYY-MM-DD'
                      )}T00:00:00.000000000`
                    : null,
            },
        })
    }

    const formatDateTitle = (value: unknown) => {
        if (!value) return ''
        const date = dayjs(value)
        return date.isValid() ? date.format('DD/MM/YYYY') : ''
    }

    // Form field definitions
    const formData: DatacentralHookFormDataItem<CreateCompanyFormValues>[] =
        useMemo(
            () => [
                {
                    colSpan: 2,
                    renderEditComponent: (control) => (
                        <div className="relative">
                            <FormField
                                control={control}
                                name="company_tax"
                                type="textfield"
                                placeholder={t('company_tax_code')}
                                label={t('company_tax_code')}
                            />
                            {isLookingUp && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                            )}
                        </div>
                    ),
                },
                {
                    colSpan: 2,
                    formItemProps: {
                        name: 'company_name',
                        type: 'textfield',
                        placeholder: t('company_name'),
                        label: t('company_name'),
                    },
                },
                {
                    colSpan: 2,
                    formItemProps: {
                        name: 'company_website',
                        type: 'textfield',
                        placeholder: t('company_website'),
                        label: t('company_website'),
                    },
                },
                {
                    colSpan: 2,
                    formItemProps: {
                        name: 'company_industry',
                        type: 'select',
                        placeholder: t('company_industry'),
                        label: t('company_industry'),
                        options: INDUSTRY_OPTIONS,
                    },
                },
                {
                    colSpan: 2,
                    renderEditComponent: (control) => {
                        const establishDate = formControl.watch(
                            'company_establish_date'
                        )

                        return (
                            <div
                                className="relative bg-white min-w-0"
                                title={formatDateTitle(establishDate)}
                            >
                                <FormItem
                                    className="w-full min-w-0 [&_[data-ui-component='TextField']]:w-full [&_.text-field-container]:w-full [&_.text-field-container]:min-w-0 [&_.text-field]:min-w-0 [&_.text-field]:pr-10 [&_.text-field]:overflow-hidden [&_.text-field]:text-ellipsis [&_.text-field]:whitespace-nowrap"
                                    name="company_establish_date"
                                    type="datepicker"
                                    control={control}
                                    placeholder={t('company_establish_date')}
                                />
                                {!establishDate && (
                                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                        {t('company_establish_date')}
                                    </span>
                                )}
                            </div>
                        )
                    },
                },
                {
                    colSpan: 2,
                    formItemProps: {
                        name: 'company_address',
                        type: 'textarea',
                        placeholder: t('company_address'),
                        label: t('company_address'),
                    },
                },
            ],
            [t, isLookingUp, formControl]
        )

    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={t('create-new-company')}
            closeOnClick={false}
        >
            <DatacentralHookForm
                formControl={formControl}
                data={formData}
                onSubmit={handleSubmitForm}
                columns={2}
                actions={[
                    {
                        title: t('cancel'),
                        variant: 'outline',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('create'),
                        type: 'submit',
                        variant: 'default',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateCompany
